<template>
  <MenuList>
    <div class="w-full pt-1.5 px-4 pb-1">
      <div class="flex items-center">
        <Avatar :src="user?.avatarUrl" :size="40" class="mr-3" />
        <div class="w-full">
          <div
            class="font-secondary font-medium text-[14px] leading-[16px] text-gray-800 mb-1"
          >
            {{ user?.name }}
          </div>
          <div
            class="w-[90%] overflow-hidden text-ellipsis font-secondary text-[12px] leading-[16px] text-gray-800 opacity-50"
          >
            {{ user?.email }}
          </div>
        </div>
      </div>
      <div v-if="isBasicAccount" class="mt-3">
        <div
          class="flex items-center font-primary leading-[18px] text-gray-800 mb-1"
        >
          <div class="mr-1 font-medium text-[13px]">
            {{ totalFilesCopy }}
          </div>
          <div class="opacity-60 text-[12px]">
            {{ pluralize('copyright', user?.totalFiles || 0) }}
          </div>
        </div>
        <div
          class="font-primary text-[12px] leading-[18px] text-gray-800 opacity-60"
        >
          <span v-if="hasReachedFileLimit">You're at the limit of Basic.</span>
          Upgrade to Pro to add unlimited copyrights
        </div>
      </div>
      <div v-else class="mt-3">
        <div
          class="flex items-center font-primary leading-[18px] text-gray-800 mb-1 last:mb-0"
        >
          <div class="mr-1 font-medium text-[13px]">
            {{ totalFilesCopy }}
          </div>
          <div class="opacity-60 text-[12px]">
            {{
              pluralize(
                'copyright',
                user?.totalFilesLimit || user?.totalFiles || 0
              )
            }}
          </div>
        </div>
        <template v-if="user?.upcomingBilling">
          <div
            class="flex items-center font-primary leading-[18px] text-gray-800 mb-1 last:mb-0"
          >
            <div class="mr-1 font-medium text-[13px]">
              {{ upcomingBillingAmount }}
            </div>
            <div class="opacity-60 text-[12px]">
              due on {{ prettyDateFormat(user.upcomingBilling.date) }}
            </div>
          </div>
        </template>
        <div
          v-if="user?.pendingSales"
          class="flex items-center font-primary leading-[18px] text-gray-800 mb-1 last:mb-0"
        >
          <div class="mr-1 font-medium text-[13px]">
            {{ user.pendingSales }}
          </div>
          <div class="opacity-60 text-[12px]">
            pending {{ pluralize('sale', user.pendingSales) }}
          </div>
        </div>
        <div
          v-if="user?.pendingTransfers"
          class="flex items-center font-primary leading-[18px] text-gray-800 mb-1 last:mb-0"
        >
          <div class="mr-1 font-medium text-[13px]">
            {{ user.pendingTransfers }}
          </div>
          <div class="opacity-60 text-[12px]">
            pending {{ pluralize('transfer', user.pendingTransfers) }}
          </div>
        </div>
        <div
          v-if="user?.pendingLicenses"
          class="flex items-center font-primary leading-[18px] text-gray-800 mb-1 last:mb-0"
        >
          <div class="mr-1 font-medium text-[13px]">
            {{ user.pendingLicenses }}
          </div>
          <div class="opacity-60 text-[12px]">
            pending {{ pluralize('license', user.pendingLicenses) }}
          </div>
        </div>
        <div
          v-if="isPro"
          class="font-secondary text-[11px] leading-[16px] text-gray-500 slant"
        >
          *Copyrights are billed one hour after they are uploaded
        </div>
      </div>
    </div>

    <MenuListSeparator />

    <template v-if="canUpgradeToPro">
      <RouterLink to="/upgrade">
        <MenuListItem icon="Dacr"> Upgrade to Pro </MenuListItem>
      </RouterLink>

      <MenuListSeparator />
    </template>

    <template v-else-if="canUpgradeLimit">
      <MenuListItem icon="Dacr" @click="handleUpgradeLimitClick">
        Upgrade your limit
      </MenuListItem>

      <MenuListSeparator />
    </template>

    <RouterLink to="/settings/account">
      <MenuListItem> Account settings </MenuListItem>
    </RouterLink>

    <a v-if="isAffiliate" :href="affiliateUrl" target="_blank">
      <MenuListItem>Affiliate dashboard</MenuListItem>
    </a>

    <a href="/download" target="_blank">
      <MenuListItem> Install Dacr app </MenuListItem>
    </a>

    <MenuListSeparator />
    <a href="https://help.dacr.com" target="_blank">
      <MenuListItem> Help center </MenuListItem>
    </a>

    <MenuListSeparator />

    <MenuListItem @click="handleSignout"> Sign out </MenuListItem>
  </MenuList>
</template>

<script setup lang="ts">
import { Avatar } from '@dacr/ui'
import { useUserStore } from '@/shared/stores/user'
import { formatPrice } from '@/shared/utils/money'
import { computed } from 'vue'
import { PlanType, UserPermission } from '@/shared/graphql/legacy/generated'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useRouter } from 'vue-router'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useBillingStore } from '@/features/billing/store'
import { MenuList, MenuListItem, MenuListSeparator } from './Menu'
import { useUploadStore } from '@/features/upload/store'
import { formatNumber } from '@/shared/utils/number'
import pluralize from 'pluralize'

const router = useRouter()
const userStore = useUserStore()
const billingStore = useBillingStore()
const uploadStore = useUploadStore()
const { open } = useDialog()

const user = computed(() => userStore.user)

const isBasicAccount = computed(
  () => !user.value?.permissions.includes(UserPermission.Pro)
)

const isPro = computed(() => billingStore.billing?.plan.type === PlanType.Pro)

const hasBundleSubscription = computed(
  () => billingStore.billing?.plan.type === PlanType.Bundle
)

const canUpgradeToPro = computed(
  () => isBasicAccount.value && user.value?.canUpgradePlan
)

const canUpgradeLimit = computed(
  () => hasBundleSubscription.value && user.value?.canUpgradePlan
)

const upcomingBillingAmount = computed(() => {
  return formatPrice({
    amount: (user.value?.upcomingBilling?.total || 0) / 100,
    currency: user.value?.upcomingBilling?.currency,
  })
})

const totalFilesCopy = computed(() =>
  typeof user.value?.totalFilesLimit === 'number'
    ? `${formatNumber(user.value?.totalFiles || 0)} of ${formatNumber(user.value?.totalFilesLimit || 0)}`
    : formatNumber(user.value?.totalFiles || 0)
)

const hasReachedFileLimit = computed(
  () =>
    typeof user.value?.totalFilesLimit === 'number' &&
    user.value?.totalFiles === user.value.totalFilesLimit
)

const isAffiliate = computed(() =>
  user.value?.permissions.includes(UserPermission.Affiliate)
)

const affiliateUrl = computed(() => import.meta.env.VITE_AFFILIATE_URL)

const prettyDateFormat = (date: number) => {
  return formatTimestamp(date, {
    formatString: user.value?.dateFormat,
  })
}

const handleUpgradeLimitClick = () => {
  document.body.click()

  if (!billingStore.canUpdateSubscription) {
    return open(Dialogs.IAP, {
      props: { type: 'change' },
    })
  }

  open(Dialogs.UpgradeBundleLimit)
}

const handleSignout = () => {
  uploadStore.showUploadBar = false
  router.push('/signout')
}
</script>

<style scoped>
.component {
  box-shadow:
    0 0 7px 0 rgb(0 0 0 / 10%),
    0 2px 3px 0 rgb(0 0 0 / 20%);
}
</style>
